import { useMemo, useCallback, useEffect } from 'react';
import { Col, Row, Form } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import { useNavigate } from 'hooks/useNavigate';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import ExtraSalesApi from 'libs/api/promo/extraSales';
import { useApi } from 'hooks/useApi';
import LinkWithParams from 'components/linkWithParams';
import { useFilter } from 'hooks/useFilter';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';

const api = new ExtraSalesApi();

export const statusColors = {
  Expired: 'black',
  Nuovo: 'yellow',
  status_to_admin: 'yellow',
  Approvato: 'green',
  Rifiutato: 'red',
  Disattiva: 'red',
};

/**
 *
 * @param {boolean} isMainPage - true if the page loads all items, false if just one
 * @returns {{
 *  columns: any,
 *  data: any,
 *  singleItem: any,
 *  loading: boolean,
 *  updateInProcess: boolean,
 *  setUpdateInProcess: func,
 *  onDelete: func,
 *  onChangeTableFilter: func
 * }} state and callbacks for ExtraSales page
 */
const useExtraSales = (isMainPage = true) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { id } = useParams();

  const {
    loading,
    data,
    singleItem,
    onDelete,
    onChangeTableFilter,
    handleDownloadExcel,
    updateInProcess,
    setUpdateInProcess,
    setSingleItem,
  } = useApi({
    resourceKey: 'extrasales',
    singleResourceKey: 'extrasale',
    api,
    isMainPage,
  });
  const { filterData } = useFilter();
  const pages = useRecoilValue(getPage);

  const handleApproveOrReject = useCallback(
    async ({ comment, publish, unpublish }) => {
      setUpdateInProcess(true);

      const data = { comment };

      if (publish) data.publish = publish;
      if (unpublish) data.unpublish = unpublish;

      try {
        if (!id) {
          navigate(UIHelper.getListRoute(pathname, 1));
          throw new Error('no ID provided.');
        } else {
          await api.handleApproveOrReject(id, { data });
          navigate(UIHelper.getListRoute(pathname));
        }
      } catch (e) {
        const errors = e.response?.data || [];
        form.setFields(ErrorHelper.getErrorsForForm(errors));
      } finally {
        setUpdateInProcess(false);
      }
    },
    [setUpdateInProcess, form, navigate, pathname, id],
  );

  const handleCheckMemberByCompany = useCallback(
    async (company_id, extrasale_hour_id) => {
      const response = await api.checkMemberByCompany(
        id,
        company_id,
        extrasale_hour_id,
      );
      return response.data;
    },
    [id],
  );

  const handleMemberApprove = useCallback(
    async ({ company_id, extrasale_hour_id, comment, confirmed }) => {
      setUpdateInProcess(true);
      try {
        const payload = { company_id, comment, confirmed };
        if (extrasale_hour_id != null && extrasale_hour_id !== '') {
          payload.extrasale_hour_id = extrasale_hour_id;
        }
        await api.memberApprove(id, payload);
        if (confirmed) {
          UIHelper.openNotificationWithIcon(
            'success',
            t('Attività approvata con successo'),
          );
        } else {
          UIHelper.openNotificationWithIcon(
            'error',
            t('Attività rifiutata con successo'),
          );
        }
      } catch (e) {
        const errors = e.response?.data || {};
        UIHelper.openNotificationWithIcon(
          'error',
          (errors?.company_id || []).join(', '),
        );
      } finally {
        setUpdateInProcess(false);
      }
    },
    [setUpdateInProcess, id, t],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        sorter: () => {},
        render: (data, row) => row.shopping_center,
        fixed: 'left',
      },
      {
        title: t('Titolo attività'),
        dataIndex: 'title',
        sorter: () => {},
      },
      {
        title: t('Inizio attività'),
        dataIndex: 'date_from',
        render: (data) => data && moment(data).format('DD MMMM YYYY'),
        sorter: () => {},
      },
      {
        title: t('Fine attività'),
        dataIndex: 'date_to',
        render: (data) => data && moment(data).format('DD MMMM YYYY'),
        sorter: () => {},
      },
      {
        title: t('Scadenza adesione'),
        dataIndex: 'membership_expiration_date',
        render: (data) => data && moment(data).format('DD MMMM YYYY'),
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 160,
        fixed: 'right',
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update ||
            row.can_destroy !== false) && (
            <Row
              gutter={16}
              justify={'end'}
              onClick={(e) => e.stopPropagation()}
            >
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col onClick={(e) => e.stopPropagation()}>
                  <LinkWithParams to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </LinkWithParams>
                </Col>
              )}
            </Row>
          ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, pages?.ability?.can_read]);

  const filters = useMemo(
    () => [
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_title',
        values: [],
        isActiveSearch: true,
      },
      {
        title: t('Catena'),
        type: 'select',
        name: 'by_chain',
        values: filterData?.chains?.map((chain) => ({
          title: chain.title,
          value: chain.value,
        })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company_id',
        isSearch: true,
        values: filterData?.companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
        inactiveValues: filterData?.inactive_companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
      },
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'shopping_center',
        values: filterData?.shopping_centers?.map((shopping_center) => ({
          title: shopping_center.title,
          value: shopping_center.value,
        })),
      },
      {
        title: t('Dal'),
        type: 'date',
        name: 'by_date_from',
        values: [],
      },
      {
        title: t('AI'),
        type: 'date',
        name: 'by_date_to',
        values: [],
      },
    ],
    [t, filterData],
  );

  useEffect(() => {
    if (id && !isMainPage) {
      const values = [
        {
          name: 'title_it',
          value: singleItem?.title_it,
        },
        {
          name: 'title_en',
          value: singleItem?.title_en,
        },
        {
          name: 'title_fr',
          value: singleItem?.title_fr,
        },
        {
          name: 'description_it',
          value: singleItem?.description_it,
        },
        {
          name: 'description_en',
          value: singleItem?.description_en,
        },
        {
          name: 'description_fr',
          value: singleItem?.description_fr,
        },
        {
          name: 'until_last',
          value: singleItem?.until_last,
        },
        {
          name: 'staff_only',
          value: singleItem?.staff_only,
        },
        {
          name: 'evidence',
          value: singleItem?.evidence,
        },
        {
          name: 'shopping_center_title',
          value: singleItem?.shopping_center_title,
        },
        {
          name: 'company_categories',
          value: singleItem?.allData?.company_categories,
        },
        {
          name: 'is_archive',
          value: singleItem?.is_archive,
        },
        {
          name: 'is_newsletter',
          value: singleItem?.is_newsletter,
        },
        {
          name: 'is_radio',
          value: singleItem?.is_radio,
        },
        {
          name: 'is_social',
          value: singleItem?.is_social,
        },
        {
          name: 'is_web',
          value: singleItem?.is_web,
        },
      ];

      singleItem?.published_at &&
        values.push({
          name: 'published_at',
          value: moment(singleItem?.published_at),
        });

      singleItem?.date_from &&
        values.push({
          name: 'date_from',
          value: moment(singleItem?.date_from),
        });

      singleItem?.date_to &&
        values.push({
          name: 'date_to',
          value: moment(singleItem?.date_to),
        });

      form.setFields(values);
    }
  }, [singleItem, form, id, isMainPage]);

  const handleCreateOrUpdate = async (values) => {
    setUpdateInProcess(true);
    try {
      const formData = new FormData();
      for (let key in values) {
        switch (key) {
          case 'contractor_ids':
          case 'company_category_ids': {
            if (values[key]) {
              values[key].forEach((id) => {
                formData.append(`extrasale[${key}][]`, id);
              });
            }
            break;
          }
          case 'times': {
            if (values[key]) {
              values[key].forEach((time, index) => {
                formData.append('time_from[]', time.time_from.format('HH:mm'));
                formData.append('time_to[]', time.time_to.format('HH:mm'));
              });
            }
            break;
          }
          case 'to':
          case 'from':
          case 'published_at': {
            if (values[key]) {
              formData.append(`extrasale[${key}]`, values[key] || '');
            }
            break;
          }
          case 'file':
            if (values[key] && values[key][0]) {
              formData.append(
                `extrasale[${key}]`,
                (values[key] &&
                  values[key][0] &&
                  values[key][0]?.originFileObj) ||
                  '',
              );
            }
            break;
          default:
            formData.append(`extrasale[${key}]`, values[key] ?? '');
            break;
        }
      }
      if (!id) {
        await api.createEvent(formData);
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.updateEvent(id, formData);
        navigate(UIHelper.getListRoute(pathname, 2));
      }
    } catch (e) {
      const errors = e.response?.data || [];
      form.setFields(
        ErrorHelper.getErrorsForForm(errors, {
          description: 'description_it',
          company_categories: 'company_category_ids',
        }),
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(() => {
    if (singleItem?.id) {
      let fieldsValues = [];
      for (let key in singleItem) {
        switch (key) {
          case 'file':
          case 'shopping_center': {
            break;
          }
          case 'allData': {
            fieldsValues.push({
              name: 'company_category_ids',
              value:
                singleItem.allData?.company_categories?.map((i) => i.id) || [],
            });
            fieldsValues.push({
              name: 'contractor_ids',
              value: singleItem.allData?.contractors?.map((i) => i.id) || [],
            });
            break;
          }
          case 'membership_expiration_date':
          case 'date_to':
          case 'date_from': {
            fieldsValues.push({
              name: key,
              value: moment(singleItem[key], 'YYYY-MM-DD'),
            });
            break;
          }
          case 'company': {
            fieldsValues.push({
              name: 'company_id',
              value: singleItem[key].id,
            });
            break;
          }
          default: {
            fieldsValues.push({
              name: key,
              value: singleItem[key],
            });
          }
        }
      }
      form.setFields(fieldsValues);
    }
  }, [singleItem]);

  const handleDeleteFile = async (data) => {
    setUpdateInProcess(true);
    try {
      const {
        file: { id },
        param,
      } = data;
      await api.deleteFile(singleItem.id, id);
      setSingleItem((s) => ({
        ...s,
        [param]: null,
      }));
    } catch (e) {
      console.log(e);
    } finally {
      setUpdateInProcess(false);
    }
  };

  return {
    form,
    columns,
    filters,
    data,
    singleItem,
    loading,
    onDelete,
    updateInProcess,
    handleCreateOrUpdate,
    handleApproveOrReject,
    handleDownloadExcel,
    onChangeTableFilter,
    filterData,
    handleDeleteFile,
    handleMemberApprove,
    handleCheckMemberByCompany,
    shoppingCenters: filterData?.shopping_centers || [],
    companyCategories: filterData?.company_categories || [],
  };
};

export default useExtraSales;
